import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AppLayout from "./components/layout/AppLayout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import JobListPage from "./pages/JobListPage";
import JobDetailPage from "./pages/JobDetailPage";
import RecordDetailPage from "./pages/RecordDetailPage";
import ReviewQueuePage from "./pages/ReviewQueuePage";
import UnresolvedQueuePage from "./pages/UnresolvedQueuePage";
import SitesPage from "./pages/SitesPage";
import PanelSourcesPage from "./pages/PanelSourcesPage";
import FieldMappingsPage from "./pages/FieldMappingsPage";
import MatchingRulesPage from "./pages/MatchingRulesPage";
import AuditLogPage from "./pages/AuditLogPage";
import HealthPage from "./pages/HealthPage";
import TrashPage from "./pages/TrashPage";
import SheetRegistryPage from "./pages/SheetRegistryPage";
import UserManagementPage from "./pages/UserManagementPage";
import AiAssistantPage from "./pages/AiAssistantPage";
import AiAuditPage from "./pages/AiAuditPage";
import "./App.css";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-sm text-zinc-400 font-mono">Loading...</p>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-sm text-zinc-400 font-mono">Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/jobs" element={<JobListPage />} />
        <Route path="/jobs/:id" element={<JobDetailPage />} />
        <Route path="/records/:id" element={<RecordDetailPage />} />
        <Route path="/review" element={<ReviewQueuePage />} />
        <Route path="/retry" element={<UnresolvedQueuePage />} />
        <Route path="/unresolved" element={<UnresolvedQueuePage />} />
        <Route
          path="/trash"
          element={
            <ProtectedRoute roles={["admin"]}>
              <TrashPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/sites"
          element={
            <ProtectedRoute roles={["admin"]}>
              <SitesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/panels"
          element={
            <ProtectedRoute roles={["admin"]}>
              <PanelSourcesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/mappings"
          element={
            <ProtectedRoute roles={["admin"]}>
              <FieldMappingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/rules"
          element={
            <ProtectedRoute roles={["admin"]}>
              <MatchingRulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route path="/health" element={<HealthPage />} />
        <Route path="/ai-assistant" element={<AiAssistantPage />} />
        <Route
          path="/ai-audit"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AiAuditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute roles={["admin"]}>
              <UserManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/sheets"
          element={
            <ProtectedRoute roles={["admin"]}>
              <SheetRegistryPage />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
